"use client";

import type React from "react";
import { useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import uploadToS3 from "../../util/uploadToS3";
import useSWRMutation from "swr/mutation";
import { cn } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import requestErrorHandler from "../../util/requestErrorHandler";
import TextArea from "antd/es/input/TextArea";
import { handleMutation } from "../../util/axios";
import { Password } from "../../components/ui/Password";


export function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    companyRegistrationNumber: "",
    password: "",
    cacCertificate: "",
    address: "",
    confirmPassword: "",
  });  

  const [uploading, setUploading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const [file, setFile] = useState<File | null>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleFileUpload = async () => {
    if (!file) return alert("Please select a file to upload.");

    setUploading(true);
    try {
      await uploadToS3({
        file,
        data: {},
        filename: file.name,
        onSuccess: (url: string) => {
          setFormData((prev) => ({ ...prev, cacCertificate: url }));
        },
        onError: (error: any) => {
          console.error("Upload failed:", error);
        },
      });
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const { trigger, isMutating } = useSWRMutation(
    `fleet/create`,
    handleMutation
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await trigger(formData);
      message.success("Registration Processing")
      navigate("/verification");
    } catch (error) {
      requestErrorHandler(error)
    }
  };

  return (
    <div className="max-w-3xl mx-auto w-full flex flex-col justify-center p-4">
      <h1 className="text-xl md:text-2xl font-bold mb-6 md:mb-20 text-center">
        Create a Fleet Owner Account
      </h1>

      <form
        onSubmit={handleSubmit}
        className={cn("space-y-8", isMutating ? "opacity-50" : "opacity-100")}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="name" className="block font-medium">
                Your name
              </label>
              <Input
                disabled={isMutating}
                id="name"
                name="name"
                placeholder="Enter Full Name"
                value={formData.name}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="block font-medium">
                Email address*
              </label>
              <Input
                disabled={isMutating}
                id="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleChange}
                required
                className="pl-3 bg-gray-200 border-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="companyName" className="block font-medium">
                Company name
              </label>
              <Input
                disabled={isMutating}
                id="companyName"
                name="companyName"
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="companyRegistration"
                className="block font-medium"
              >
                Company registration number
              </label>
              <Input
                disabled={isMutating}
                id="companyRegistrationNumber"
                name="companyRegistrationNumber"
                placeholder="Enter company number"
                value={formData.companyRegistrationNumber}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>            
          </div>

          <div className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="cacCertificate" className="block font-medium">
                CAC Certificate
              </label>
              <div className="border border-dashed border-blue-500 rounded-md p-2 flex flex-col items-center justify-center bg-blue-50">
                <input
                  disabled={isMutating}
                  type="file"
                  id="cacCertificate"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="cacCertificate"
                  className="flex items-center gap-2 text-blue-500 cursor-pointer"
                >
                  <Upload className="w-5" />
                  <span>{file ? file.name : "Upload"}</span>
                </label>
                {file && (
                  <button
                    type="button"
                    className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md"
                    onClick={handleFileUpload}
                    disabled={uploading}
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="phone" className="block font-medium">
                Phone number
              </label>
              <Input
                disabled={isMutating}
                id="phone"
                name="phone"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={handleChange}
                className="pl-3 bg-gray-200 border-none"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="address" className="block font-medium">
                Business address
              </label>
              <TextArea
                disabled={isMutating}
                id="address"
                name="address"
                placeholder="Enter business address"
                value={formData.address}
                onChange={handleTextChange}
                className="pl-3 bg-gray-200 border-none"
              ></TextArea>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block font-medium">
                Password*
              </label>
              <Password
                disabled={isMutating}
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                className="pl-3 bg-gray-200 border-none"
              />
            </div>                      

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block font-medium">
                Confirm password*
              </label>
              <Password
                disabled={isMutating}
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="pl-3 bg-gray-200 border-none"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-center">
          <Button
            disabled={isMutating}
            type="submit"
            className="text-lg w-full md:w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-2"
          >
            {isMutating ? (<Loader2 className="animate-spin h-5 w-5" />) : "Sign up"}
          </Button>
        </div>
      </form>
    </div>
  );
}
